import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../utils/api";
import { useFeedback } from "../hooks/useFeedback";
import Spinner from "../components/ui/Spinner";
import CollapsibleCard from "../components/ui/CollapsibleCard";

/* ── Helpers date ──────────────────────────────────────────────────── */
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const expiryDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  d.setDate(d.getDate() + 730);
  return d.toLocaleDateString("fr-FR");
};

/* ══════════════════════════════════════════════════════════════════
   Token Syrinix
   ══════════════════════════════════════════════════════════════════ */
function TokenSyrinix() {
  const [token, setToken] = useState("");
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, showFeedback] = useFeedback();

  const fetchToken = async () => {
    setLoading(true);
    setToken("");
    setVisible(false);
    try {
      const data = await apiFetch("/api/get_token");
      setToken(data.token);
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  const masked = token ? `${token.slice(0, 4)}••••••••${token.slice(-4)}` : "";

  return (
    <CollapsibleCard icon="🔑" title="Token Syrinix">
      <div className="p-4 flex flex-col gap-3">
        <button
          onClick={fetchToken}
          disabled={loading}
          className="btn btn-md btn-primary self-start"
        >
          {loading ? <><Spinner /> Récupération…</> : "Récupérer un token"}
        </button>

        {feedback && (
          <div className={`feedback-${feedback.type === "success" ? "success" : "error"}`}>
            {feedback.msg}
          </div>
        )}

        {token && (
          <div className="flex items-center gap-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <span className="flex-1 break-all text-gray-700">{visible ? token : masked}</span>
            <button
              onClick={() => setVisible(v => !v)}
              className="btn btn-sm btn-ghost shrink-0"
            >
              {visible ? "Masquer" : "Afficher"}
            </button>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Token Kizeo
   ══════════════════════════════════════════════════════════════════ */
function TokenKizeo() {
  const [status, setStatus] = useState(null);
  const [newToken, setNewToken] = useState("");
  const [newDate, setNewDate] = useState("");
  const [showNewToken, setShowNewToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, showFeedback] = useFeedback(6000);

  const loadStatus = () =>
    apiFetch("/api/kizeo/token_status").then(setStatus).catch(() => {});

  useEffect(() => { loadStatus(); }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!newToken || !newDate) {
      showFeedback("error", "Les deux champs sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/api/kizeo/update_token_date", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_token: newToken, new_date: newDate }),
      });
      setNewToken("");
      setNewDate("");
      showFeedback("success", "Token et date mis à jour avec succès.");
      loadStatus();
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setSaving(false);
    }
  };

  const badge = status?.expired
    ? { cls: "badge-error",  icon: "🚨", text: "Expiré" }
    : status?.alert
      ? { cls: "badge-paused", icon: "⚠️", text: "Alerte" }
      : { cls: "badge-active", icon: "✅", text: "Valide" };

  return (
    <CollapsibleCard
      icon="🔐"
      title="Token Kizeo"
      headerRight={status ? <span className={`badge ${badge.cls}`}>{badge.icon} {badge.text}</span> : null}
    >
      <div className="p-4 flex flex-col gap-4">
        {status === null ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : (
          <dl className="text-sm flex flex-col gap-1.5">
            <div className="flex justify-between">
              <dt className="text-gray-500">Émission</dt>
              <dd className="font-medium">{fmtDate(status.token_date)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Expiration</dt>
              <dd className="font-medium">{expiryDate(status.token_date)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Jours restants</dt>
              <dd className={`font-medium ${
                status.expired ? "text-red-600" : status.alert ? "text-amber-600" : "text-emerald-600"
              }`}>
                {status.days_remaining ?? "—"}
              </dd>
            </div>
          </dl>
        )}

        <div className="divider" />

        <p className="text-sm font-semibold text-gray-700">Mettre à jour le token</p>
        <form onSubmit={handleUpdate} className="flex flex-col gap-2">
          <div className="relative">
            <input
              type={showNewToken ? "text" : "password"}
              className="input pr-20"
              placeholder="Nouveau token"
              value={newToken}
              onChange={e => setNewToken(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNewToken(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
            >
              {showNewToken ? "Masquer" : "Afficher"}
            </button>
          </div>
          <input
            type="date"
            className="input"
            value={newDate}
            onChange={e => setNewDate(e.target.value)}
          />
          {feedback && (
            <div className={`feedback-${feedback.type === "success" ? "success" : "error"}`}>
              {feedback.msg}
            </div>
          )}
          <button type="submit" disabled={saving} className="btn btn-md btn-primary">
            {saving ? <><Spinner /> Mise à jour…</> : "Enregistrer"}
          </button>
        </form>
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Documentation algorithmique
   ══════════════════════════════════════════════════════════════════ */
function Documentation() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || content !== null) return;
    setLoading(true);
    fetch("/api/docs/algorithmes")
      .then(r => {
        // Réponse texte brut, pas JSON : ne peut pas passer par apiFetch, mais
        // doit quand même déclencher le retour à l'écran de connexion sur 401
        // comme apiFetch (cf. remontée Benoît 12/08/2026).
        if (r.status === 401) window.dispatchEvent(new Event("v3:auth-expired"));
        return r.text();
      })
      .then(t => setContent(t))
      .catch(() => setContent("Erreur de chargement de la documentation."))
      .finally(() => setLoading(false));
  }, [open]);

  const download = () => {
    if (!content) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([content], { type: "text/markdown" }));
    a.download = "algorithmes_syrinix_kizeo.md";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <CollapsibleCard icon="📖" title="Documentation algorithmique" onOpenChange={setOpen}>
      <div className="p-4 flex flex-col gap-3">
        {loading && <p className="text-sm text-gray-400">Chargement…</p>}
        {content && !loading && (
          <>
            <div className="flex justify-end">
              <button onClick={download} className="btn btn-sm btn-secondary">
                ⬇ Télécharger .md
              </button>
            </div>
            <pre className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded-lg p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap leading-relaxed">
              {content}
            </pre>
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Sauvegardes
   ══════════════════════════════════════════════════════════════════ */
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  const units = ["o", "Ko", "Mo", "Go"];
  let n = bytes, i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

function Backups() {
  const [backups, setBackups] = useState(null);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [feedback, showFeedback] = useFeedback(6000);

  const loadBackups = () =>
    apiFetch("/api/admin/backup").then(setBackups).catch(() => setBackups([]));

  useEffect(() => { loadBackups(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await apiFetch("/api/admin/backup", { method: "POST" });
      showFeedback("success", "Sauvegarde créée.");
      loadBackups();
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = (filename) => {
    window.open(`/api/admin/backup/${filename}/download`, "_blank", "noopener,noreferrer");
  };

  const handleRestore = async (filename) => {
    if (!window.confirm(
      `Restaurer "${filename}" ? Cette action remplace toutes les données actuelles par le contenu de cette sauvegarde. Une sauvegarde de sécurité de l'état actuel est prise automatiquement avant.`
    )) return;
    setRestoring(filename);
    try {
      await apiFetch(`/api/admin/backup/${filename}/restore`, { method: "POST" });
      showFeedback("success", "Restauration effectuée.");
      loadBackups();
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <CollapsibleCard icon="💾" title="Sauvegardes">
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Sauvegarde automatique quotidienne + déclenchement manuel. Rétention 14 jours.
          </p>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="btn btn-md btn-primary shrink-0"
          >
            {creating ? <><Spinner /> Création…</> : "Créer une sauvegarde"}
          </button>
        </div>

        {feedback && (
          <div className={`feedback-${feedback.type === "success" ? "success" : "error"}`}>
            {feedback.msg}
          </div>
        )}

        {backups === null ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : backups.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune sauvegarde pour l'instant.</p>
        ) : (
          <div className="flex flex-col divide-y divide-gray-100">
            {backups.map((b) => (
              <div key={b.filename} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium text-gray-700">{fmtDateTime(b.created_at)}</div>
                  <div className="text-gray-400">{fmtSize(b.size_bytes)}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleDownload(b.filename)}
                    className="btn btn-sm btn-secondary"
                  >
                    ⬇ Télécharger
                  </button>
                  <button
                    onClick={() => handleRestore(b.filename)}
                    disabled={restoring === b.filename}
                    className="btn btn-sm btn-danger"
                  >
                    {restoring === b.filename ? <><Spinner /> Restauration…</> : "Restaurer"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Anomalies POK
   ══════════════════════════════════════════════════════════════════ */
function PokAnomalies() {
  const [anomalies, setAnomalies] = useState(null);

  const loadAnomalies = () =>
    apiFetch("/api/pok/anomalies").then(setAnomalies).catch(() => setAnomalies([]));

  useEffect(() => { loadAnomalies(); }, []);

  return (
    <CollapsibleCard
      icon="⚠️"
      title="Anomalies POK"
      headerRight={
        anomalies && anomalies.length > 0
          ? <span className="badge badge-paused">{anomalies.length}</span>
          : null
      }
    >
      <div className="p-4 flex flex-col gap-3">
        <p className="text-sm text-gray-500">
          Essais envoyés par l'app POK et rejetés par le serveur (métadonnées invalides ou conflit
          d'identifiant entre deux essais différents).
        </p>

        {anomalies === null ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : anomalies.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune anomalie pour l'instant.</p>
        ) : (
          <div className="flex flex-col divide-y divide-gray-100">
            {[...anomalies].reverse().map((a, i) => (
              <div key={i} className="py-2 text-sm flex flex-col gap-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-700">{a.reason}</span>
                  <span className="text-gray-400 shrink-0">{fmtDateTime(a.timestamp)}</span>
                </div>
                {a.essai_id && <div className="text-gray-500 font-mono text-xs">{a.essai_id}</div>}
                <div className="text-gray-500">{a.detail}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Application POK — dernière version (04/09/2026, demande Jordan)
   Publie l'APK proposé automatiquement en mise à jour dans l'app (GET
   /api/pok/version + /api/pok/apk, cf. pok_routes.py) — la version affichée
   ici est extraite directement du binaire uploadé par le backend
   (pyaxmlparser), jamais ressaisie à la main.
   ══════════════════════════════════════════════════════════════════ */
function PokApkRelease() {
  const [status, setStatus] = useState(undefined); // undefined = chargement, null = aucune version
  const [uploading, setUploading] = useState(false);
  const [feedback, showFeedback] = useFeedback(6000);
  const fileInputRef = useRef(null);

  const loadStatus = () =>
    apiFetch("/api/pok/apk/status").then(setStatus).catch(() => setStatus(null));

  useEffect(() => { loadStatus(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await apiFetch("/api/pok/apk", { method: "POST", body: formData });
      showFeedback("success", `Version ${data.version_name} (code ${data.version_code}) publiée.`);
      setStatus(data);
    } catch (err) {
      showFeedback("error", err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <CollapsibleCard
      icon="📱"
      title="Application POK — dernière version"
      headerRight={status ? <span className="badge badge-active">v{status.version_name}</span> : null}
    >
      <div className="p-4 flex flex-col gap-3">
        <p className="text-sm text-gray-500">
          L'APK envoyé ici est proposé automatiquement comme mise à jour dans l'app POK
          (vérification au lancement + bouton "Mettre à jour"). La version affichée est extraite
          directement du fichier APK, jamais ressaisie à la main.
        </p>

        {status === undefined ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : status === null ? (
          <p className="text-sm text-gray-400">Aucune version publiée pour l'instant.</p>
        ) : (
          <dl className="text-sm flex flex-col gap-1.5">
            <div className="flex justify-between">
              <dt className="text-gray-500">Version</dt>
              <dd className="font-medium">{status.version_name} (code {status.version_code})</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Publiée le</dt>
              <dd className="font-medium">{fmtDateTime(status.uploaded_at)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Taille</dt>
              <dd className="font-medium">{fmtSize(status.size_bytes)}</dd>
            </div>
          </dl>
        )}

        {feedback && (
          <div className={`feedback-${feedback.type === "success" ? "success" : "error"}`}>
            {feedback.msg}
          </div>
        )}

        <label className="btn btn-md btn-primary self-start cursor-pointer">
          {uploading ? <><Spinner /> Envoi…</> : "Publier un nouvel APK"}
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Page Admin
   ══════════════════════════════════════════════════════════════════ */
export default function Admin() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <TokenSyrinix />
        <TokenKizeo />
      </div>
      <Backups />
      <PokApkRelease />
      <PokAnomalies />
      <Documentation />
    </div>
  );
}
